// «Agrupar por área» (shared/API.md §37, 2026-10-17). Twin of the Apple/Android/Windows
// `AreaGrouping` written from the same contract on the same day.
//
// When the option is on, the DM part of the list (the `direct_messages` category) is split into
// one section per área of the OTHER person, sorted by name with Spanish collation, then a last
// «Otros» section holding people without an área AND every group message. Inside a section the
// order is the list's ordinary one (newest activity first — the rows arrive already ordered and
// keep that order). A section with no rows is not drawn. With nobody in an área the list is drawn
// exactly as with the option off: no lone «Otros» header (most accounts today — design for
// mostly-empty).
//
// Área sections are local: their collapse state lives on this device (the server's categories know
// nothing about them), keyed by `sectionId`.
import { channelListSection, type ChannelListSection, type DisplayChannel } from './channelListDisplayModels';
import { t } from '../i18n/i18n';

/** The section type of one área's DMs. `categoryDisplayName` shows its `displayName` verbatim. */
export const AREA_SECTION_TYPE = 'area';
/** The section type of the last «Otros» section. */
export const OTHERS_SECTION_TYPE = 'area_others';
/** Every área section id starts with this — never a server category id (those are `<kind>_<user>_<team>` / 26-char ids). */
export const AREA_SECTION_PREFIX = 'area:';
export const OTHERS_SECTION_ID = 'area::others';
/** Core names the área's channel `area-<satori_area_id>` (§37); the display name follows renames, the name does not. */
export const AREA_CHANNEL_PREFIX = 'area-';

/** Whether a channel `name` is a Core-kept área channel. */
export function isAreaChannelName(name: string | null | undefined): boolean {
  return !!name && name.startsWith(AREA_CHANNEL_PREFIX) && name.length > AREA_CHANNEL_PREFIX.length;
}

/** The id an área's section carries (its collapse key). The área NAME, trimmed: two ids never share one name in Core. */
export function sectionId(area: string): string {
  return `${AREA_SECTION_PREFIX}${area.trim()}`;
}

export function isAreaSectionId(id: string): boolean {
  return id.startsWith(AREA_SECTION_PREFIX);
}

const collator = new Intl.Collator('es', { sensitivity: 'base', numeric: true });

/** Spanish collation: «Ádministración» sorts with A, case and accents do not split the order. */
export function compareAreaNames(a: string, b: string): number {
  return collator.compare(a, b) || (a < b ? -1 : a > b ? 1 : 0);
}

function areaOf(row: DisplayChannel): string | null {
  if (row.type !== 'D') return null; // a group message has no single "other person": «Otros»
  const area = row.dmPartnerArea?.trim();
  return area ? area : null;
}

export interface AreaGroupingOptions {
  enabled: boolean;
  /** Área sections (and «Otros») the reader collapsed on this device. */
  collapsedIds: ReadonlySet<string>;
}

/**
 * The list's sections with the `direct_messages` category split by área. Every other section is
 * returned untouched and in place; the split sections take the DM category's place.
 */
export function group(sections: readonly ChannelListSection[], options: AreaGroupingOptions): ChannelListSection[] {
  if (!options.enabled) return [...sections];
  const out: ChannelListSection[] = [];
  for (const section of sections) {
    if (section.type !== 'direct_messages') {
      out.push(section);
      continue;
    }
    const buckets = new Map<string, DisplayChannel[]>();
    const others: DisplayChannel[] = [];
    for (const row of section.rows) {
      const area = areaOf(row);
      if (!area) {
        others.push(row);
        continue;
      }
      const bucket = buckets.get(area);
      if (bucket) bucket.push(row);
      else buckets.set(area, [row]);
    }
    if (buckets.size === 0) {
      // Nobody in an área: exactly the list without the option (no lone «Otros» header).
      out.push(section);
      continue;
    }
    for (const area of [...buckets.keys()].sort(compareAreaNames)) {
      const id = sectionId(area);
      out.push(channelListSection({ id, displayName: area, type: AREA_SECTION_TYPE, collapsed: options.collapsedIds.has(id), rows: buckets.get(area)! }));
    }
    if (others.length) {
      out.push(
        channelListSection({
          id: OTHERS_SECTION_ID,
          displayName: t('web.area.others'),
          type: OTHERS_SECTION_TYPE,
          collapsed: options.collapsedIds.has(OTHERS_SECTION_ID),
          rows: others,
        }),
      );
    }
  }
  return out;
}

/** Namespace form, for `AreaGrouping.group(...)` call sites. */
export const AreaGrouping = { group, isAreaChannelName, sectionId, isAreaSectionId, compareAreaNames, OTHERS_SECTION_ID } as const;
